/**
 * The step-up challenge broker.
 *
 * CLAUDE.md §6/§13 require a *recent* second factor before a payment executes,
 * before a vendor's bank details change or are revealed, and before a period
 * closes. The server enforces that by refusing the request with
 * `403 ERR_STEP_UP_REQUIRED`, whose `detail` names the endpoint that answers it.
 *
 * Answering that refusal is a cross-cutting concern, not a screen's job. Every
 * guarded action lives behind a different button on a different screen, and
 * bolting a code prompt onto each one guarantees the next guarded action ships
 * without it. So the transport intercepts the refusal, asks *this* broker for a
 * code, and — once the code is accepted — replays the original request. The
 * user's payment run completes; nothing they typed is lost. That last part is
 * the whole point: before this existed, a step-up refusal meant a filled-in
 * form and a message pointing at a profile page that could not help.
 *
 * Three properties are deliberate:
 *
 *  - **Single flight.** A screen that fires three guarded requests at once must
 *    produce one prompt, not three stacked dialogs. Every caller that arrives
 *    while a prompt is open joins it and is answered by the same outcome.
 *  - **A rejected code re-prompts, it does not cancel.** The server burns the
 *    code it was shown (`VerifyTOTPOnce`), so a replayed one is refused even
 *    though it still looks valid in the authenticator — "wait for the next code"
 *    is a real state, and the pending requests have to survive it.
 *  - **`ERR_MFA_REQUIRED` is not a wrong code.** It means the account has no
 *    authenticator to re-prove, so re-prompting would loop forever. The waiters
 *    are released and the app routes to enrolment instead.
 *
 * The broker knows nothing about HTTP: the app installs a verifier that calls
 * its realm's step-up endpoint (`/api/v1/auth/step-up` or
 * `/admin/api/v1/auth/step-up`) and swaps in the returned access token.
 */

import { ref, type Ref } from 'vue'

import { ERROR_CODES, toProblemDetails } from './problem'

/** What a waiting request learns when the challenge finishes. */
export type StepUpOutcome =
  /** A code was accepted; the caller should retry its request. */
  | 'granted'
  /** The user closed the prompt; the caller should surface its original error. */
  | 'dismissed'
  /** There is no second factor to prove; the app is routing to enrolment. */
  | 'enrol'

/** Why a submitted code was refused — each needs different wording. */
export type StepUpFailure =
  /** Wrong, expired, or already spent. Re-promptable. */
  | 'code'
  /** The shared login lockout budget is exhausted. Not re-promptable. */
  | 'lockout'
  /** No authenticator enrolled. Handled by routing, never by re-prompting. */
  | 'enrol'
  /** Anything else the server said. Shown verbatim rather than guessed at. */
  | 'other'

/** The live challenge, or `null` when nothing is being asked. */
export interface StepUpPrompt {
  /** An opaque token the app maps to a sentence naming the action. */
  reason: string
  /** How many requests are waiting behind this one prompt. */
  waiting: number
  /** A code is in flight; the form is disabled. */
  busy: boolean
  /** Codes refused so far in this prompt. Drives the lockout warning. */
  attempts: number
  failure: StepUpFailure | null
  /** The server's own prose for the last failure, when it had any. */
  detail: string
}

/** Exchanges a one-time code for a stepped-up session. Throws on refusal. */
export type StepUpVerifier = (code: string) => Promise<unknown>

/** Called when the account has no authenticator: route to enrolment. */
export type StepUpEnrolmentHandler = (detail: string) => void

/**
 * Prose matching, reluctantly.
 *
 * The tenant realm answers an exhausted lockout budget with
 * `401 ERR_UNAUTHENTICATED` — the same status and code it uses for a wrong code
 * — and puts the distinction only in the title and detail ("Too many failed
 * attempts", "try again after …"). The admin realm answers `429`, which needs
 * no guessing. Without this test a locked-out user would be told to check their
 * code and would keep trying, which is exactly the loop §13's lockout exists to
 * stop. If the server ever gives lockout its own code, delete this.
 */

/** Which kind of refusal a failed step-up was. */
export function classifyStepUpFailure(error: unknown): StepUpFailure {
  const problem = toProblemDetails(error)
  if (problem === null) return 'other'
  if (problem.code === ERROR_CODES.mfaRequired) return 'enrol'
  // The machine code is the contract (§7). This used to fall back to matching the
  // human-readable title on a 401, because the tenant realm returned lockout and a
  // wrong password under the same code — which would have started reporting every
  // lockout as a bad code the first time that sentence was translated, and §6 ships
  // a Dhivehi catalogue. Both realms now answer ERR_LOCKED_OUT.
  if (problem.code === ERROR_CODES.lockedOut) return 'lockout'
  if (problem.status === 429) return 'lockout'
  if (problem.status === 401) return 'code'
  return 'other'
}

export interface StepUpBroker {
  /** The open challenge, for the dialog to render. */
  readonly prompt: Readonly<Ref<StepUpPrompt | null>>
  /** Install the realm's step-up call. Until it is set, challenges are dismissed. */
  setVerifier(verify: StepUpVerifier | null): void
  /** Install the "send them to enrolment" behaviour (needs the router). */
  setEnrolmentHandler(handler: StepUpEnrolmentHandler | null): void
  /** Ask for a code, joining any prompt already open. */
  challenge(reason: string): Promise<StepUpOutcome>
  /** Answer the open challenge. Re-prompts on refusal; never throws. */
  submit(code: string): Promise<void>
  /** Give up on the open challenge; waiters get `dismissed`. */
  cancel(): void
  /** Test seam: dismiss anything pending without touching installed handlers. */
  reset(): void
}

export function createStepUpBroker(): StepUpBroker {
  const prompt = ref<StepUpPrompt | null>(null)
  let verifier: StepUpVerifier | null = null
  let enrolment: StepUpEnrolmentHandler | null = null
  let waiters: ((outcome: StepUpOutcome) => void)[] = []

  /** Close the prompt and answer everyone waiting behind it, exactly once. */
  function settle(outcome: StepUpOutcome): void {
    const pending = waiters
    waiters = []
    prompt.value = null
    for (const resolve of pending) resolve(outcome)
  }

  function challenge(reason: string): Promise<StepUpOutcome> {
    // No verifier means no way to answer — better to hand the caller back its
    // original 403 than to open a dialog whose submit button does nothing.
    if (verifier === null) return Promise.resolve<StepUpOutcome>('dismissed')

    const open = prompt.value
    if (open === null) {
      prompt.value = { reason, waiting: 1, busy: false, attempts: 0, failure: null, detail: '' }
    } else {
      // The reason stays the one that opened the prompt: it named the action the
      // user actually pressed, and a later background request must not rewrite it.
      open.waiting += 1
    }

    return new Promise<StepUpOutcome>((resolve) => {
      waiters.push(resolve)
    })
  }

  async function submit(code: string): Promise<void> {
    const open = prompt.value
    const verify = verifier
    if (open === null || verify === null || open.busy) return

    const trimmed = code.trim()
    if (trimmed === '') return

    open.busy = true
    open.failure = null
    open.detail = ''

    try {
      await verify(trimmed)
      settle('granted')
    } catch (error) {
      const failure = classifyStepUpFailure(error)
      const detail = toProblemDetails(error)?.detail?.trim() ?? ''

      if (failure === 'enrol') {
        // Re-prompting is hopeless: there is no authenticator behind the account.
        settle('enrol')
        enrolment?.(detail)
        return
      }

      // Still the same prompt, still the same waiting requests: only the form
      // reopens for another code.
      const live = prompt.value
      if (live !== null) {
        live.busy = false
        live.attempts += 1
        live.failure = failure
        live.detail = detail
      }
    }
  }

  return {
    prompt,
    setVerifier(verify) {
      verifier = verify
    },
    setEnrolmentHandler(handler) {
      enrolment = handler
    },
    challenge,
    submit,
    cancel() {
      settle('dismissed')
    },
    reset() {
      settle('dismissed')
    },
  }
}
